import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, Clock, Send, Shield, Eye, Clipboard, Type } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const API_URL = '';

/**
 * ExamMode - Locked assessment/test environment
 * Features:
 * - Fullscreen locked UI (no sidebar/nav)
 * - Countdown timer (for timed tests)
 * - Tab switch detection with warnings
 * - Paste blocking/detection
 * - Typing speed tracking
 * - Auto-submit on timeout/close/tab switch limit
 */
export default function ExamMode({ assessment, student, onExit }) {
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [tabSwitchLog, setTabSwitchLog] = useState([]);
  const [pasteCount, setPasteCount] = useState(0);
  const [pasteLog, setPasteLog] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMsg, setWarningMsg] = useState('');
  const [keystrokeData, setKeystrokeData] = useState({ totalChars: 0, startTime: null, intervals: [] });

  const maxTabSwitches = assessment.max_tab_switches ?? 2;
  const timeLimit = assessment.time_limit; // in minutes
  const lockdownMode = assessment.lockdown_mode ?? false;
  const submissionIdRef = useRef(null);
  const startedAtRef = useRef(null);
  const tabSwitchRef = useRef(0);
  const autoSubmitTriggered = useRef(false);

  // Start the assessment
  const handleStart = async () => {
    try {
      console.log('Starting assessment:', assessment.id, 'for student:', student?.id);
      
      // Retry up to 3 times
      let lastError = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await fetch(`${API_URL}/api/student-portal/start-assessment/${assessment.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ student_id: student?.id })
          });
          // Clone response before reading to avoid "body stream already read" errors
          const resClone = res.clone();
          let data;
          try {
            data = await res.json();
          } catch (jsonErr) {
            // If json parsing fails, try reading from clone
            try {
              const text = await resClone.text();
              data = JSON.parse(text);
            } catch {
              console.error(`Attempt ${attempt}: Failed to parse response`);
              lastError = 'Server returned invalid response';
              if (attempt < 3) {
                await new Promise(r => setTimeout(r, 1000));
                continue;
              }
              toast.error('Failed to start assessment. Please refresh and try again.');
              return;
            }
          }
          if (!res.ok) {
            console.error(`Attempt ${attempt} failed:`, data);
            lastError = data.detail || 'Server error';
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, 1000));
              continue;
            }
            toast.error(data.detail || 'Failed to start assessment.');
            return;
          }
          if (data.submission_id) {
            submissionIdRef.current = data.submission_id;
            startedAtRef.current = new Date();
            setStarted(true);
            const effectiveTimeLimit = data.time_limit || timeLimit;
            if (effectiveTimeLimit) {
              if (data.started_at) {
                // Ensure UTC parsing - append Z if missing to avoid timezone offset issues
                const startedAtStr = data.started_at.endsWith('Z') ? data.started_at : data.started_at + 'Z';
                const elapsed = (Date.now() - new Date(startedAtStr).getTime()) / 1000;
                const remaining = Math.max(0, effectiveTimeLimit * 60 - elapsed);
                setTimeLeft(Math.floor(remaining));
              } else {
                setTimeLeft(effectiveTimeLimit * 60);
              }
            }
            setKeystrokeData(prev => ({ ...prev, startTime: Date.now() }));
            return; // Success, exit function
          } else {
            toast.error('Unexpected response from server.');
            return;
          }
        } catch (fetchErr) {
          console.error(`Attempt ${attempt} network error:`, fetchErr);
          lastError = fetchErr.message;
          if (attempt < 3) {
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
        }
      }
      toast.error(`Failed after 3 attempts: ${lastError}`);
    } catch (err) {
      console.error('Start assessment error:', err);
      toast.error('Failed to start assessment. Please try again.');
    }
  };

  // Timer countdown - using setTimeout for reliability (avoids stale closure issues)
  useEffect(() => {
    if (!started || timeLeft === null || submitted) return;
    if (timeLeft <= 0) {
      handleAutoSubmit('timeout');
      return;
    }
    const timer = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [started, timeLeft, submitted]);

  // Tab visibility detection
  useEffect(() => {
    if (!started || submitted) return;
    const handleVisibility = () => {
      if (document.hidden) {
        const newCount = tabSwitchRef.current + 1;
        tabSwitchRef.current = newCount;
        setTabSwitchCount(newCount);
        setTabSwitchLog(prev => [...prev, { timestamp: new Date().toISOString(), event: 'tab_hidden' }]);

        if (maxTabSwitches > 0 && newCount >= maxTabSwitches) {
          handleAutoSubmit('tab_switch');
        } else if (maxTabSwitches > 0) {
          const remaining = maxTabSwitches - newCount;
          setWarningMsg(`Tab switch detected! ${remaining} warning(s) remaining before auto-submit.`);
          setShowWarning(true);
          setTimeout(() => setShowWarning(false), 5000);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [started, submitted, maxTabSwitches]);

  // Prevent navigation (beforeunload)
  useEffect(() => {
    if (!started || submitted) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your test will be auto-submitted if you leave.';
      handleAutoSubmit('browser_close');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [started, submitted]);

  // Block keyboard shortcuts
  useEffect(() => {
    if (!started || submitted) return;
    const handleKeyDown = (e) => {
      // Block Ctrl+C on selected text (but allow within input fields)
      if (e.ctrlKey && (e.key === 'c' || e.key === 'C') && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
      }
      // Block Ctrl+Tab, Alt+Tab can't be blocked in browser
      if (e.ctrlKey && e.key === 'Tab') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [started, submitted]);

  // Auto-submit handler
  const handleAutoSubmit = useCallback(async (reason) => {
    if (autoSubmitTriggered.current || submitted) return;
    autoSubmitTriggered.current = true;
    toast.warning(`Test auto-submitted: ${reason === 'timeout' ? 'Time is up' : reason === 'tab_switch' ? 'Tab switch limit reached' : 'Browser close detected'}`);
    await submitAnswers(true, reason);
  }, [submitted, answers]);

  // Submit answers
  const submitAnswers = async (isAuto = false, autoReason = null) => {
    if (submitting || submitted) return;
    setSubmitting(true);

    const timeSpent = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current.getTime()) / 1000) : 0;
    const typingSpeed = keystrokeData.startTime && keystrokeData.totalChars > 0
      ? Math.round((keystrokeData.totalChars / ((Date.now() - keystrokeData.startTime) / 60000)))
      : 0;

    const answersArray = assessment.questions?.map((q, i) => ({
      question_number: i + 1,
      answer: answers[i] ?? '',
      question_type: q.type || 'short_answer'
    })) || [];

    try {
      const res = await fetch(`${API_URL}/api/student-portal/submit-assessment/${assessment.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: student.id,
          answers: answersArray,
          tab_switch_count: tabSwitchRef.current,
          tab_switch_log: tabSwitchLog,
          paste_event_count: pasteCount,
          paste_events: pasteLog,
          typing_speed_avg: typingSpeed,
          time_spent_seconds: timeSpent,
          auto_submitted: isAuto,
          auto_submit_reason: autoReason,
        })
      });
      const resClone = res.clone();
      let data;
      try {
        data = await res.json();
      } catch {
        data = JSON.parse(await resClone.text());
      }
      if (data.success) {
        setSubmitted(true);
        toast.success(isAuto ? 'Test auto-submitted.' : 'Test submitted successfully!');
      } else {
        toast.error(data.detail || 'Failed to submit');
        autoSubmitTriggered.current = false;
      }
    } catch (err) {
      toast.error('Submission failed. Please try again.');
      autoSubmitTriggered.current = false;
    }
    setSubmitting(false);
  };

  // Handle paste events
  const handlePaste = (e, questionIdx) => {
    const pastedText = e.clipboardData?.getData('text') || '';
    setPasteCount(prev => prev + 1);
    setPasteLog(prev => [...prev, {
      timestamp: new Date().toISOString(),
      field: `question_${questionIdx + 1}`,
      text_length: pastedText.length
    }]);
    toast.warning('Paste detected and logged.');
    // Don't block paste, but log it
  };

  // Handle typing
  const handleTyping = (e, questionIdx) => {
    const val = e.target.value;
    setAnswers(prev => ({ ...prev, [questionIdx]: val }));
    setKeystrokeData(prev => ({
      ...prev,
      totalChars: prev.totalChars + 1,
    }));
  };

  const formatTime = (seconds) => {
    if (seconds === null) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isTimeCritical = timeLeft !== null && timeLeft < 120;

  // ---- Submitted screen ----
  if (submitted) {
    return (
      <div className="fixed inset-0 z-[9999] bg-gray-900 flex items-center justify-center" data-testid="exam-submitted">
        <Card className="w-full max-w-md mx-4 border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <Send className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Test Submitted</h2>
            <p className="text-gray-600 mb-6">Your answers have been recorded successfully.</p>
            {tabSwitchRef.current > 0 && (
              <p className="text-sm text-amber-600 mb-4">Tab switches recorded: {tabSwitchRef.current}</p>
            )}
            <Button onClick={onExit} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="exit-exam-btn">
              Return to Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Pre-start screen ----
  if (!started) {
    return (
      <div className="fixed inset-0 z-[9999] bg-gray-900 flex items-center justify-center" data-testid="exam-prestart">
        <Card className="w-full max-w-lg mx-4 border-0 shadow-2xl">
          <CardHeader className="bg-emerald-700 text-white rounded-t-lg">
            <CardTitle className="text-xl">{assessment.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2 text-sm text-gray-700">
              <p><strong>Subject:</strong> {assessment.subject}</p>
              <p><strong>Questions:</strong> {assessment.num_questions}</p>
              <p><strong>Total Marks:</strong> {assessment.total_marks}</p>
              {timeLimit && <p><strong>Time Limit:</strong> {timeLimit} minutes</p>}
              {assessment.instructions && <p><strong>Instructions:</strong> {assessment.instructions}</p>}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2" data-testid="exam-rules">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Exam Rules
              </h3>
              <ul className="text-sm text-amber-700 space-y-1">
                {lockdownMode && <li className="flex items-center gap-2"><Eye className="w-3 h-3" /> Portal will be locked during the test</li>}
                {timeLimit && <li className="flex items-center gap-2"><Clock className="w-3 h-3" /> Auto-submit when time runs out</li>}
                {maxTabSwitches > 0 && <li className="flex items-center gap-2"><AlertTriangle className="w-3 h-3" /> {maxTabSwitches} tab switch warning(s) — then auto-submit</li>}
                <li className="flex items-center gap-2"><Clipboard className="w-3 h-3" /> Copy-paste activity is monitored</li>
                <li className="flex items-center gap-2"><Type className="w-3 h-3" /> Typing patterns are tracked</li>
              </ul>
            </div>

            <Button onClick={handleStart} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base" data-testid="start-exam-btn">
              Start Test
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Active test ----
  return (
    <div className="fixed inset-0 z-[9999] bg-gray-100 flex flex-col overflow-hidden" data-testid="exam-active">
      {/* Warning overlay */}
      {showWarning && (
        <div className="absolute inset-0 z-[10000] bg-red-900/80 flex items-center justify-center animate-pulse" data-testid="tab-warning">
          <div className="bg-white rounded-xl p-8 max-w-md text-center shadow-2xl">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-700 mb-2">Warning!</h3>
            <p className="text-gray-700">{warningMsg}</p>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="bg-gray-900 text-white px-6 py-3 flex items-center justify-between shrink-0" data-testid="exam-topbar">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-emerald-400" />
          <span className="font-semibold">{assessment.title}</span>
          <span className="text-xs text-gray-400">| {assessment.subject} | {assessment.num_questions} questions</span>
        </div>
        <div className="flex items-center gap-4">
          {tabSwitchRef.current > 0 && (
            <span className="text-xs text-amber-400" data-testid="tab-switch-indicator">
              Tab switches: {tabSwitchRef.current}/{maxTabSwitches || '-'}
            </span>
          )}
          {timeLeft !== null && (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-mono ${isTimeCritical ? 'bg-red-600 animate-pulse' : 'bg-gray-700'}`} data-testid="exam-timer">
              <Clock className="w-4 h-4" />
              {formatTime(timeLeft)}
            </div>
          )}
          <Button
            onClick={() => submitAnswers(false)}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-700 h-9 px-4"
            data-testid="submit-exam-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Test'}
          </Button>
        </div>
      </div>

      {/* Questions */}
      <div className="flex-1 overflow-y-auto px-4 py-6" data-testid="exam-questions">
        <div className="max-w-3xl mx-auto space-y-6">
          {assessment.questions?.map((q, idx) => (
            <Card key={idx} className="border shadow-sm" data-testid={`question-${idx}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <span className="bg-emerald-100 text-emerald-700 font-bold text-sm w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{q.question || q.text}</p>
                    {q.marks && <span className="text-xs text-gray-500">[{q.marks} mark{q.marks > 1 ? 's' : ''}]</span>}
                  </div>
                </div>

                {/* MCQ */}
                {(q.type === 'mcq' || q.type === 'multiple_choice') && q.options && (
                  <div className="space-y-2 ml-11">
                    {q.options.map((opt, oi) => (
                      <label key={oi} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${answers[idx] === opt ? 'bg-emerald-50 border-emerald-300' : 'hover:bg-gray-50 border-transparent'}`}>
                        <input
                          type="radio"
                          name={`q-${idx}`}
                          value={opt}
                          checked={answers[idx] === opt}
                          onChange={() => setAnswers(prev => ({ ...prev, [idx]: opt }))}
                          className="accent-emerald-600"
                          data-testid={`q${idx}-opt${oi}`}
                        />
                        <span className="text-sm">{opt}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* True/False */}
                {q.type === 'true_false' && (
                  <div className="flex gap-3 ml-11">
                    {['True', 'False'].map(val => (
                      <label key={val} className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border transition-colors ${answers[idx] === val ? 'bg-emerald-50 border-emerald-300' : 'hover:bg-gray-50 border-gray-200'}`}>
                        <input type="radio" name={`q-${idx}`} value={val}
                          checked={answers[idx] === val}
                          onChange={() => setAnswers(prev => ({ ...prev, [idx]: val }))}
                          className="accent-emerald-600"
                          data-testid={`q${idx}-${val.toLowerCase()}`}
                        />
                        <span className="text-sm font-medium">{val}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Short answer / Essay */}
                {(!q.type || q.type === 'short_answer' || q.type === 'essay' || q.type === 'open_ended') && (
                  <textarea
                    className="w-full ml-0 mt-2 p-3 border rounded-lg text-sm resize-y min-h-[80px] focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Type your answer here..."
                    value={answers[idx] || ''}
                    onChange={(e) => handleTyping(e, idx)}
                    onPaste={(e) => handlePaste(e, idx)}
                    data-testid={`q${idx}-answer`}
                  />
                )}

                {/* Fill in blank */}
                {(q.type === 'fill_in_blank' || q.type === 'fill_blank' || q.type === 'gap_fill') && (
                  <input
                    type="text"
                    className="ml-11 mt-2 p-2 border rounded-lg text-sm w-64 focus:ring-2 focus:ring-emerald-500"
                    placeholder="Your answer..."
                    value={answers[idx] || ''}
                    onChange={(e) => handleTyping(e, idx)}
                    onPaste={(e) => handlePaste(e, idx)}
                    data-testid={`q${idx}-answer`}
                  />
                )}
              </CardContent>
            </Card>
          ))}

          {/* Bottom submit */}
          <div className="pb-8 flex justify-center">
            <Button
              onClick={() => submitAnswers(false)}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8 text-base"
              data-testid="submit-exam-bottom-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Test'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
